import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'

interface MetaTagsProps {
  title?: string
  description?: string | null | undefined
  keywords?: string
  ogTitle?: string | null | undefined
  ogDescription?: string | null | undefined
  ogImage?: string | null | undefined
  ogUrl?: string
  twitterCard?: string
  twitterTitle?: string | null | undefined
  twitterDescription?: string | null | undefined
  twitterImage?: string | null | undefined
  alternateLanguages?: Array<{ hreflang: string; href: string }>
  canonicalUrl?: string
  /** When true, asks crawlers not to index this page (draft / private / unlisted). */
  noIndex?: boolean
}

export default function MetaTags({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  twitterCard = 'summary_large_image',
  twitterTitle,
  twitterDescription,
  twitterImage,
  alternateLanguages,
  canonicalUrl,
  noIndex = false
}: MetaTagsProps) {
  const { t, i18n } = useTranslation()

  // Use provided values or fall back to defaults
  const finalTitle = title || t('meta.title')
  const finalDescription = description || t('meta.description')
  const finalUrl = ogUrl || window.location.href
  const finalCanonicalUrl = canonicalUrl || window.location.href

  const currentLang = i18n.language

  return (
    <Helmet>
      <html lang={currentLang} />
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Language and Canonical */}
      <link rel="canonical" href={finalCanonicalUrl} />
      {alternateLanguages?.map(({ hreflang, href }) => (
        <link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
      ))}

      {/* Open Graph tags */}
      <meta property="og:title" content={ogTitle || finalTitle} />
      <meta property="og:description" content={ogDescription || finalDescription} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta property="og:url" content={finalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={currentLang} />

      {/* Twitter Card tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={twitterTitle || finalTitle} />
      <meta name="twitter:description" content={twitterDescription || finalDescription} />
      {twitterImage && <meta name="twitter:image" content={twitterImage} />}
    </Helmet>
  )
}
